import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Icon,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    Stack,
    Text
} from "@chakra-ui/react";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Loading from "components/Loading/Loading";
import { checkErrors, hasError, isValidAitNumber, isValidCEP } from "utils/forms-utils";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { daysOfWeek, userAccessTimesService } from "../../../../services/userAccessTimesService";
import { ROUTES } from "../../../../constants/routes-path";
import { FaClock } from "react-icons/fa";
import InputMask from "react-input-mask";
import ListPageCardUserHeader from "../ListPageCardUserHeader";
import { userService } from "../../../../services/userService";
import { validateCPF, validateEmail, validateTime } from "../../../../utils";

function UserAccessTimesNew({ data }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const { state } = location.state || {};
    const [urlSearchParams] = useSearchParams();
    const [userData, setUserData] = useState(undefined);
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const translationKey = "settings.user-access-times";

    const [formData, setFormData] = useState(undefined);
    const [inputError, setInputError] = useState({});

    const parentId = urlSearchParams.get("parent_id");

    const ids = useMemo(() => ({
        id: "id",
        dayOfWeekStart: "day-of-week-start",
        dayOfWeekEnd: "day-of-week-end",
        timeStart: "time-start",
        timeEnd: "time-end",
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (data) => ({
        [ids.id]: {
            value: data?.id || "",
            validations: [],
        },
        [ids.dayOfWeekStart]: {
            value: data?.dia_semana_inicio || "",
            validations: [{ required: true }],
        },
        [ids.dayOfWeekEnd]: {
            value: data?.dia_semana_fim || "",
            validations: [{ required: true }],
        },
        [ids.timeStart]: {
            value: data?.hora_inicio || "",
            validations: [{ required: true }],
        },
        [ids.timeEnd]: {
            value: data?.hora_fim || "",
            validations: [{ required: true }],
        },
    });

    const validatorMap = {
        [ids.timeStart]: {
            validate: validateTime,
            errorKey: 'time-invalid',
        },
        [ids.timeEnd]: {
            validate: validateTime,
            errorKey: 'time-invalid',
        }
    };

    const fetchData = async () => {
        try {
            if (id) {
                const userAccessTimesDetails = await userAccessTimesService.find(id);
                setFormData(initFormData(userAccessTimesDetails));
            } else {
                setFormData(initFormData());
                const user = await userService.find(parentId);
                setUserData(user);
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
            redirectToList();
        }
    }

    useEffect(() => {
        fetchData();
    }, [id]);

    const redirectToList = () => {
        navigate(`${ROUTES.usersAccessTimePath}?parent_id=${parentId}`, {
            state: { parentId }
        });
    }

    const handleSave = async () => {
        if (isFormInvalid) return;

        try {
            const data = toDTO();
            if (id) {
                await userAccessTimesService.update(id, data);
                showToast({ title: t(`${translationKey}.messages.success.edit`), status: "success" });
            } else {
                await userAccessTimesService.create(data);
                showToast({ title: t(`${translationKey}.messages.success.create`), status: "success" });
            }
            redirectToList();
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }

    };

    const handleInputChange = (e) => {
        const { id, type } = e.target;
        const value = type === "checkbox" ? e.target.checked : e.target.value;

        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value }
        };

        let newErrors = checkErrors({
            formData: newFormData,
            inputError: inputError,
            name: id
        });

        const validator = validatorMap[id];
        if (validator) {
            if (!validator.validate(value)) {
                newErrors = {
                    ...newErrors,
                    [id]: t(`${translationKey}.messages.errors.${validator.errorKey}`)
                };
            } else {
                const { [id]: removed, ...rest } = newErrors;
                newErrors = rest;
            }
        }

        setFormData(newFormData);
        setInputError(newErrors);
    };


    const toDTO = () => ({
        dia_semana_inicio: formData[ids.dayOfWeekStart].value,
        dia_semana_fim: formData[ids.dayOfWeekEnd].value,
        hora_inicio: formData[ids.timeStart].value,
        hora_fim: formData[ids.timeEnd].value,
        id_usuario_horario: parentId,
    });

    return (
        <Flex gap="5" direction="column" minH="89vh" align="center" pt={{ sm: "150px", md: "90px" }}>
            <Card>
                {userData && (
                    <ListPageCardUserHeader userData={userData} />
                )}
                <ListPageCardHeader
                    title={t(`${translationKey}.${id ? "edit" : "new"}.title`)}
                    subtitle={t(`${translationKey}.${id ? "edit" : "new"}.subtitle`)}
                />
                <CardBody px="22px">
                    {formData ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.dayOfWeekStart]}>
                                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                                        {t(`${translationKey}.new.fields.day-of-week-start.label`)}
                                    </FormLabel>
                                    <Select
                                        fontSize="sm"
                                        id={ids.dayOfWeekStart}
                                        value={formData[ids.dayOfWeekStart].value}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">{t(`${translationKey}.new.fields.day-of-week-start.placeholder`)}</option>
                                        {Object.values(daysOfWeek).map((option, key) => (
                                            <option key={key} value={option.value}>
                                                {t(`${translationKey}.new.days-of-week.${option.label}`)}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {t(inputError[ids.dayOfWeekStart])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={inputError[ids.dayOfWeekEnd]}>
                                    <FormLabel fontSize="xs" fontWeight="bold" mb="10px">
                                        {t(`${translationKey}.new.fields.day-of-week-end.label`)}
                                    </FormLabel>
                                    <Select
                                        fontSize="sm"
                                        id={ids.dayOfWeekEnd}
                                        value={formData[ids.dayOfWeekEnd].value}
                                        onChange={handleInputChange}
                                    >
                                        <option
                                            value="">{t(`${translationKey}.new.fields.day-of-week-end.placeholder`)}</option>
                                        {Object.values(daysOfWeek).map((option, key) => (
                                            <option key={key} value={option.value}>
                                                {t(`${translationKey}.new.days-of-week.${option.label}`)}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                    {t(inputError[ids.dayOfWeekEnd])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.timeStart]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${translationKey}.new.fields.time-start.label`)}
                                    </FormLabel>
                                    <InputGroup>
                                        <InputMask
                                            mask="99:99"
                                            value={formData[ids.timeStart].value}
                                            onChange={handleInputChange}
                                            maskChar={null}
                                        >
                                            {(inputProps) => (
                                                <Input
                                                    {...inputProps}
                                                    id={ids.timeStart}
                                                    fontSize="sm"
                                                    variant="main"
                                                    type="text"
                                                    placeholder={t(`${translationKey}.new.fields.time-start.placeholder`)}
                                                    maxLength={5}
                                                />
                                            )}
                                        </InputMask>
                                        <InputRightElement>
                                            <Icon
                                                as={FaClock}
                                                w={4}
                                                h={4}
                                                color="gray.500"
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                    <FormErrorMessage>
                                        {t(inputError[ids.timeStart])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isRequired isInvalid={inputError[ids.timeEnd]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t(`${translationKey}.new.fields.time-end.label`)}
                                    </FormLabel>
                                    <InputGroup>
                                        <InputMask
                                            mask="99:99"
                                            value={formData[ids.timeEnd].value}
                                            onChange={handleInputChange}
                                            maskChar={null}
                                        >
                                            {(inputProps) => (
                                                <Input
                                                    {...inputProps}
                                                    fontSize="sm"
                                                    variant="main"
                                                    id={ids.timeEnd}
                                                    type="text"
                                                    placeholder={t(`${translationKey}.new.fields.time-end.placeholder`)}
                                                    maxLength={5}
                                                />
                                            )}
                                        </InputMask>
                                        <InputRightElement>
                                            <Icon
                                                as={FaClock}
                                                w={4}
                                                h={4}
                                                color="gray.500"
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                    <FormErrorMessage>
                                        {t(inputError[ids.timeEnd])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack w="100%" direction="row" justify="space-between">
                                <Button
                                    w="125px"
                                    variant="no-effects"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={redirectToList}
                                >
                                    <Text>
                                        {t("system.actions.cancel")}
                                    </Text>
                                </Button>

                                <Button
                                    w="125px"
                                    variant="dark"
                                    fontSize="sm"
                                    isDisabled={isFormInvalid}
                                    onClick={handleSave}
                                    _hover={isFormInvalid ? {} : undefined}
                                >
                                    <Text>
                                        {t("system.actions.save")}
                                    </Text>
                                </Button>
                            </Stack>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );

};

export default UserAccessTimesNew;